// ============================================================
// TERROR ENGINE — DIALOGUE ENGINE
// Universal visual novel layer. Used by Willie, Vyren, dungeon
// NPCs, faction reps — and any future visual novel game.
//
// Two modes:
//   <DialogueBox>    — typewriter, multi-line, speaker label, choices
//   <DialogueToast>  — quick auto-dismiss message (dungeon hints etc)
//
// Visual novel ready:
//   Pass portrait={src} to show a character portrait alongside text.
//   Pass choices={[{label, onSelect}]} to show branching choices.
// ============================================================

import { useState, useEffect } from "react";
import { SD } from "./theme";

// ── Full dialogue box — typewriter, portrait, choices ─────────
export function DialogueBox({
  lines,              // string[] — array of lines to cycle through
  label  = "",        // speaker name shown above text
  color  = SD.gold,   // accent color — border, text glow, label
  portrait = null,    // optional CDN url for speaker portrait image
  choices = null,     // optional [{ label, onSelect }] shown at end
  onClose,            // called when done or dismissed
  typeSpeed = 24,     // ms per character
}) {
  const [lineIdx, setLineIdx] = useState(0);
  const [typed,   setTyped]   = useState("");
  const [typing,  setTyping]  = useState(true);

  const current = lines[lineIdx] || "";
  const atEnd   = lineIdx >= lines.length - 1 && !typing;
  const showChoices = atEnd && choices && choices.length > 0;

  // Typewriter effect
  useEffect(() => {
    setTyped(""); setTyping(true);
    let i = 0;
    const t = setInterval(() => {
      i++;
      setTyped(current.slice(0, i));
      if (i >= current.length) { clearInterval(t); setTyping(false); }
    }, typeSpeed);
    return () => clearInterval(t);
  }, [lineIdx, current, typeSpeed]);

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (typing)         { setTyped(current); setTyping(false); }
        else if (!atEnd)    { setLineIdx(i => i + 1); }
        else if (!choices)  { onClose?.(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [typing, atEnd, current, choices, onClose]);

  const advance = () => {
    if (typing)        { setTyped(current); setTyping(false); }
    else if (!atEnd)   { setLineIdx(i => i + 1); }
    else if (!choices) { onClose?.(); }
  };

  return (
    // role="dialog" and not "alertdialog": a dialogue box is a
    // conversation the player advances at their own pace, not an
    // interruption demanding an answer. aria-live on the typed line
    // is what actually makes it readable — a typewriter effect emits
    // one character at a time, and without `polite` a screen reader
    // either says nothing or stutters the sentence letter by letter.
    <div role="dialog" aria-label={label ? `${label} speaking` : "Dialogue"} style={{
      position: "fixed", bottom: 32, left: "50%",
      transform: "translateX(-50%)",
      zIndex: 300, width: "min(580px, 92vw)",
      fontFamily: SD.fontDisplay,
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${color}08, rgba(4,2,14,0.96))`,
        border: `1px solid ${color}55`,
        borderRadius: 12, padding: "16px 20px",
        boxShadow: `0 0 28px ${color}22`,
      }}>
        {/* Portrait + text row */}
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {portrait && (
            <img
              key={portrait}
              src={portrait}
              alt={label}
              onError={e => e.target.style.display = "none"}
              style={{
                width: 64, height: 64, borderRadius: 8,
                objectFit: "cover", imageRendering: "pixelated",
                border: `1px solid ${color}44`, flexShrink: 0,
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {label && (
              <div style={{
                fontSize: 8, letterSpacing: 3,
                color: `${color}99`, marginBottom: 8, fontWeight: 900,
              }}>{label.toUpperCase()}</div>
            )}
            <div aria-live="polite" aria-atomic="true" style={{
              fontSize: 13, color: color, fontStyle: "italic",
              lineHeight: 1.75, minHeight: 24,
              textShadow: `0 0 8px ${color}22`,
            }}>
              {/* The FULL line, not the typed prefix, so a reader
                  hears the sentence once and whole. The typewriter is
                  a visual effect and it has no business being an
                  audio one. */}
              <span className="sr-only">{current}</span>
              <span aria-hidden="true">{typed}
              {typing && <span style={{ opacity: 0.4 }}>▌</span>}
              </span>
            </div>
          </div>
        </div>

        {/* Choices */}
        {showChoices && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
            {choices.map((c, i) => (
              <button key={i} onClick={c.onSelect} style={{
                padding: "8px 16px", borderRadius: 8, textAlign: "left",
                background: `${color}11`, border: `1px solid ${color}44`,
                color: color, fontFamily: SD.fontDisplay, fontSize: 11,
                cursor: "pointer", letterSpacing: 1,
                transition: "all 0.15s",
              }}>
                ◆ {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        {!showChoices && (
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginTop: 10,
          }}>
            <div style={{ display: "flex", gap: 4 }}>
              {lines.map((_, i) => (
                <div key={i} style={{
                  width: i === lineIdx ? 16 : 5, height: 5,
                  borderRadius: 3, transition: "width 0.2s",
                  background: i === lineIdx ? color : `${color}44`,
                }} />
              ))}
            </div>
            <div onClick={advance} style={{
              fontSize: 9, color: `${color}88`, cursor: "pointer",
              letterSpacing: 2,
            }}>
              {atEnd ? "[ CLOSE ]" : "[ NEXT · SPACE ]"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Toast — quick auto-dismiss hint or dungeon message ────────
export function DialogueToast({
  text,
  color    = SD.purpleLight,
  duration = 4000,          // ms before auto-dismiss
  onDismiss,
}) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => { setVisible(false); onDismiss?.(); }, duration);
    return () => clearTimeout(t);
  }, [text, duration, onDismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={() => { setVisible(false); onDismiss?.(); }}
      style={{
        position: "fixed", bottom: 100, left: "50%",
        transform: "translateX(-50%)",
        zIndex: 200, cursor: "pointer",
        background: `linear-gradient(135deg, rgba(4,2,14,0.95), rgba(10,5,30,0.95))`,
        border: `1px solid ${color}55`,
        borderRadius: 12, padding: "14px 24px",
        maxWidth: "min(90vw, 440px)", textAlign: "center",
        boxShadow: `0 0 24px ${color}22`,
        fontFamily: SD.fontDisplay,
        animation: "fadeIn 0.3s ease",
      }}
    >
      <div style={{
        fontSize: 13, color,
        fontStyle: "italic", lineHeight: 1.8,
        textShadow: `0 0 8px ${color}44`,
      }}>
        {text}
      </div>
      <div style={{ fontSize: 9, color: SD.starDim, marginTop: 8, letterSpacing: 2 }}>
        tap to dismiss
      </div>
    </div>
  );
}

// ── useDialogue hook — manage multi-line NPC dialogue state ──
export function useDialogue() {
  const [dialogue, setDialogue] = useState(null); // { lines, label, color, portrait, choices }

  const openDialogue  = (config) => setDialogue(config);
  const closeDialogue = ()       => setDialogue(null);

  return { dialogue, openDialogue, closeDialogue };
}
